import Cookies from "js-cookie";
import {
  CLIENT_CSRF_COOKIE_NAME,
  getClientCookieOptions,
  readConsumerChannel,
  retireLegacyCsrfCookie,
  type ConsumerChannel,
} from "./auth-client";
import type { LibraryProfile } from "./cw-browser-session";

/** Controls new library adoption only. Existing selected aliases retain library
 * reauthentication even after rollback; they never fall back to native login. */
export function libraryLoginEnabled(): boolean {
  return process.env.NEXT_PUBLIC_CW_LIBRARY_LOGIN_ENABLED === "true";
}

export async function signInNativeCompatibility(
  email: string,
  password: string,
  assertCurrent: () => void,
  selectedChannel?: ConsumerChannel,
): Promise<LibraryProfile> {
  const channel = selectedChannel ?? (await readConsumerChannel());
  assertCurrent();
  // An existing selected alias, including a rejected one, cannot be silently
  // superseded by a native cookie which would lose the server's precedence.
  if (channel === "library" || channel === "explicit")
    throw new Error("Selected session requires library login.");
  const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/v1/login/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username: email, password }),
    referrerPolicy: "origin-when-cross-origin",
    mode: "cors",
    credentials: "include",
  });
  const payload: unknown = await response.json().catch(() => null);
  assertCurrent();
  if (
    !response.ok ||
    typeof payload !== "object" ||
    payload === null ||
    !("token" in payload) ||
    typeof payload.token !== "string" ||
    !payload.token.trim() ||
    !("user" in payload) ||
    typeof payload.user !== "object" ||
    payload.user === null ||
    !("id" in payload.user) ||
    !(
      (typeof payload.user.id === "number" &&
        Number.isSafeInteger(payload.user.id) &&
        payload.user.id > 0) ||
      (typeof payload.user.id === "string" && payload.user.id.trim().length > 0)
    )
  )
    throw new Error("Native login response unavailable.");
  if (
    "csrf_token" in payload &&
    payload.csrf_token !== undefined &&
    typeof payload.csrf_token !== "string"
  )
    throw new Error("Native login response unavailable.");
  const options = getClientCookieOptions();
  Cookies.set("auth_token", payload.token, options);
  retireLegacyCsrfCookie();
  if ("csrf_token" in payload && typeof payload.csrf_token === "string" && payload.csrf_token)
    Cookies.set(CLIENT_CSRF_COOKIE_NAME, payload.csrf_token, options);
  return payload.user as LibraryProfile;
}
